import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { Search, ArrowLeft, ChevronLeft, ChevronRight, Edit3, Trash2, X, Save, FileText, AlertTriangle, Hash, Eye, Zap, Tag, BarChart3 } from 'lucide-react';
import { searchAdminQuestions, updateAdminQuestion, deleteAdminQuestion, getDistinctQuestionValues, detectScenarios, bulkSetScenario, getScenarioStats, computeMobileFlag } from '../api/endpoints';
import { toast } from 'sonner';

const DIFFICULTIES = ['1', '2', '3', '4'];
const YEARS = [1, 2, 3, 4, 5, 6];

const INDEX_TO_LETTER = { 0: 'A', 1: 'B', 2: 'C', 3: 'D', 4: 'E' };
const LETTER_TO_INDEX = { A: 0, B: 1, C: 2, D: 3, E: 4 };

// Capitalise each word for display (e.g. "general medicine" → "General Medicine")
const capitalize = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
};

const AdminQuestionsPage = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  // Search & filter state
  const [searchText, setSearchText] = useState('');
  const [idSearch, setIdSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Dynamic dropdown values from database
  const [dbCategories, setDbCategories] = useState([]);
  const [dbSubCategories, setDbSubCategories] = useState([]);

  // Results state
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Edit modal state
  const [editQuestion, setEditQuestion] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk Scenario Tagger state
  const [scenarioPhrase, setScenarioPhrase] = useState('');
  const [scenarioPreview, setScenarioPreview] = useState(null);
  const [scenarioPreviewing, setScenarioPreviewing] = useState(false);
  const [scenarioApplying, setScenarioApplying] = useState(false);
  const [scenarioStats, setScenarioStats] = useState(null);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [computingMobile, setComputingMobile] = useState(false);
  const [showBulkTools, setShowBulkTools] = useState(false);

  useEffect(() => {
    document.title = `Question Manager | ${tenant?.name || 'MedMCQ'}`;
  }, [tenant]);

  // Load distinct values and initial questions on mount
  useEffect(() => {
    loadDistinctValues();
    fetchQuestions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDistinctValues = async () => {
    try {
      const res = await getDistinctQuestionValues();
      setDbCategories(res.data.categories || []);
      setDbSubCategories(res.data.sub_categories || []);
    } catch (err) {
      console.error('Failed to load distinct values:', err);
    }
  };

  const fetchQuestions = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const params = { page: targetPage, per_page: perPage };
      if (idSearch.trim()) params.question_id = idSearch.trim();
      else if (searchText.trim()) params.q = searchText.trim();
      if (categoryFilter) params.category = categoryFilter;
      if (subCategoryFilter) params.sub_category = subCategoryFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (yearFilter) params.year = yearFilter;

      const res = await searchAdminQuestions(params);
      const data = res.data;
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setPage(data.page || 1);
      setHasSearched(true);
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to load questions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [searchText, idSearch, categoryFilter, subCategoryFilter, difficultyFilter, yearFilter, perPage]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    fetchQuestions(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    fetchQuestions(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Edit ──────────────────────────────────────────────────
  const openEdit = (q) => {
    setEditQuestion(q);
    setEditForm({
      question: q.question || '',
      option0: q.options?.[0] || '',
      option1: q.options?.[1] || '',
      option2: q.options?.[2] || '',
      option3: q.options?.[3] || '',
      option4: q.options?.[4] || '',
      correctAnswer: INDEX_TO_LETTER[q.correct_answer] || 'A',
      category: q.category || '',
      sub_category: q.sub_category || '',
      year: q.year ?? '',
      difficulty: q.difficulty || '1',
      explanation: q.explanation || '',
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editQuestion) return;
    setSaving(true);
    try {
      const options = [editForm.option0, editForm.option1, editForm.option2, editForm.option3];
      if (editForm.option4?.trim()) options.push(editForm.option4);

      const payload = {
        question: editForm.question,
        options,
        correct_answer: LETTER_TO_INDEX[editForm.correctAnswer] ?? 0,
        category: editForm.category,
        sub_category: editForm.sub_category,
        year: editForm.year ? parseInt(editForm.year, 10) : null,
        difficulty: editForm.difficulty,
        explanation: editForm.explanation,
      };

      await updateAdminQuestion(editQuestion.id, payload);
      toast.success('Question updated successfully');
      setEditQuestion(null);
      fetchQuestions(page);
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAdminQuestion(deleteTarget.id);
      toast.success('Question deleted');
      setDeleteTarget(null);
      fetchQuestions(page);
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
    }
  };

  // ── Bulk Scenario Tagger ─────────────────────────────────
  const loadScenarioStats = async () => {
    try {
      const res = await getScenarioStats();
      setScenarioStats(res.data);
    } catch (err) {
      console.error('Failed to load scenario stats:', err);
    }
  };

  const handleScenarioPreview = async () => {
    if (!scenarioPhrase.trim()) return;
    setScenarioPreviewing(true);
    setScenarioPreview(null);
    try {
      const res = await bulkSetScenario({ phrase: scenarioPhrase.trim(), preview: true });
      setScenarioPreview(res.data);
    } catch (err) {
      toast.error('Preview failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setScenarioPreviewing(false);
    }
  };

  const handleScenarioApply = async (setTo) => {
    if (!scenarioPhrase.trim()) return;
    setScenarioApplying(true);
    try {
      const res = await bulkSetScenario({ phrase: scenarioPhrase.trim(), set_scenario: setTo, preview: false });
      toast.success(`Updated ${res.data.modified} questions — scenario set to ${setTo ? 'Yes' : 'No'}`);
      setScenarioPreview(null);
      setScenarioPhrase('');
      loadScenarioStats();
    } catch (err) {
      toast.error('Failed to apply: ' + (err.response?.data?.detail || err.message));
    } finally {
      setScenarioApplying(false);
    }
  };

  const handleAutoDetect = async () => {
    setAutoDetecting(true);
    try {
      const res = await detectScenarios();
      toast.success(`Auto-detected ${res.data.scenarios_detected} scenario questions out of ${res.data.total_processed} processed`);
      loadScenarioStats();
    } catch (err) {
      toast.error('Auto-detect failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAutoDetecting(false);
    }
  };

  const handleComputeMobile = async () => {
    setComputingMobile(true);
    try {
      const res = await computeMobileFlag();
      toast.success(`Mobile flags computed: ${res.data.mobile_suitable} suitable, ${res.data.not_suitable} not suitable`);
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setComputingMobile(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────
  const truncate = (str, len = 80) =>
    str && str.length > len ? str.slice(0, len) + '...' : str || '';

  const startItem = (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total);

  const hasActiveFilters = categoryFilter || subCategoryFilter || difficultyFilter || yearFilter || searchText || idSearch;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-cyan-400 text-white rounded-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Question Manager</h1>
              <p className="text-lg opacity-90">Search, edit and delete questions in your database</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Admin
            </button>
          </div>
        </div>

        {/* Bulk Tools Toggle */}
        <div className="mb-4">
          <button
            onClick={() => { setShowBulkTools(!showBulkTools); if (!showBulkTools && !scenarioStats) loadScenarioStats(); }}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-200 transition-colors"
            style={{ boxShadow: 'var(--shadow-card)' }}
          >
            <Tag size={16} className="text-purple-500" />
            {showBulkTools ? 'Hide Bulk Tools' : 'Bulk Tools (Scenario, Mobile)'}
          </button>
        </div>

        {/* Bulk Tools Panel */}
        {showBulkTools && (
          <div className="bg-white rounded-xl p-6 mb-6 border-l-4 border-purple-500" style={{ boxShadow: 'var(--shadow-card)' }}>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <Tag size={20} className="text-purple-500" />
              Bulk Question Tools
            </h2>

            {/* Scenario Stats */}
            {scenarioStats && (
              <div className="grid grid-cols-4 gap-3 mb-5">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{scenarioStats.scenario?.toLocaleString()}</p>
                  <p className="text-xs text-green-600 font-medium">Scenario</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{scenarioStats.theory?.toLocaleString()}</p>
                  <p className="text-xs text-blue-600 font-medium">Theory</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{scenarioStats.untagged?.toLocaleString()}</p>
                  <p className="text-xs text-amber-600 font-medium">Untagged</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-700">{scenarioStats.total?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 font-medium">Total</p>
                </div>
              </div>
            )}

            {/* Auto-Detect & Mobile Buttons */}
            <div className="flex gap-3 mb-5">
              <button
                onClick={handleAutoDetect}
                disabled={autoDetecting}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
              >
                <Zap size={16} />
                {autoDetecting ? 'Detecting...' : 'Auto-Detect Scenarios (14 patterns)'}
              </button>
              <button
                onClick={handleComputeMobile}
                disabled={computingMobile}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
              >
                <BarChart3 size={16} />
                {computingMobile ? 'Computing...' : 'Compute Mobile Flags'}
              </button>
            </div>

            {/* Phrase Search */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Search by Phrase & Tag as Scenario</h3>
              <p className="text-xs text-gray-500 mb-3">Enter a phrase to find in question text, preview matches, then set them all as Scenario or Theory.</p>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder='e.g. "year old", "patient presents", "on examination"'
                  value={scenarioPhrase}
                  onChange={(e) => setScenarioPhrase(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleScenarioPreview(); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
                <button
                  onClick={handleScenarioPreview}
                  disabled={scenarioPreviewing || !scenarioPhrase.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
                >
                  <Eye size={16} />
                  {scenarioPreviewing ? 'Searching...' : 'Preview'}
                </button>
              </div>

              {/* Preview Results */}
              {scenarioPreview && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    Found <span className="text-purple-600 font-bold">{scenarioPreview.match_count?.toLocaleString()}</span> questions containing "{scenarioPhrase}"
                  </p>
                  {scenarioPreview.samples?.length > 0 && (
                    <div className="space-y-2 mb-3">
                      <p className="text-xs text-gray-500 font-medium">Sample matches:</p>
                      {scenarioPreview.samples.map((s, i) => (
                        <div key={s.id || i} className="bg-white border border-gray-200 rounded p-2 text-xs text-gray-700">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mr-2 ${s.scenario === true ? 'bg-green-100 text-green-700' : s.scenario === false ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {s.scenario === true ? 'SCENARIO' : s.scenario === false ? 'THEORY' : 'UNTAGGED'}
                          </span>
                          {s.question?.length > 150 ? s.question.slice(0, 150) + '...' : s.question}
                        </div>
                      ))}
                    </div>
                  )}
                  {scenarioPreview.match_count > 0 && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleScenarioApply(true)}
                        disabled={scenarioApplying}
                        className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                      >
                        {scenarioApplying ? 'Applying...' : `Set all ${scenarioPreview.match_count.toLocaleString()} as Scenario`}
                      </button>
                      <button
                        onClick={() => handleScenarioApply(false)}
                        disabled={scenarioApplying}
                        className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)' }}
                      >
                        {scenarioApplying ? 'Applying...' : `Set all ${scenarioPreview.match_count.toLocaleString()} as Theory`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-xl p-6 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            {/* Search bars row */}
            <div className="flex gap-3">
              {/* Text search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search questions & explanations..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                />
              </div>
              {/* ID search */}
              <div className="relative w-72">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by ID..."
                  value={idSearch}
                  onChange={(e) => setIdSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 text-white rounded-lg font-medium transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Filters row */}
            <div className="flex gap-3 flex-wrap items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Category:</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                >
                  <option value="">All Categories</option>
                  {dbCategories.map((c) => (
                    <option key={c} value={c}>{capitalize(c)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Sub-Category:</label>
                <select
                  value={subCategoryFilter}
                  onChange={(e) => { setSubCategoryFilter(e.target.value); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                >
                  <option value="">All Sub-Categories</option>
                  {dbSubCategories.map((s) => (
                    <option key={s} value={s}>{capitalize(s)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Difficulty:</label>
                <select
                  value={difficultyFilter}
                  onChange={(e) => { setDifficultyFilter(e.target.value); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                >
                  <option value="">All</option>
                  {DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>Level {d}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Year:</label>
                <select
                  value={yearFilter}
                  onChange={(e) => { setYearFilter(e.target.value); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                >
                  <option value="">All</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>Year {y}</option>
                  ))}
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchText('');
                    setIdSearch('');
                    setCategoryFilter('');
                    setSubCategoryFilter('');
                    setDifficultyFilter('');
                    setYearFilter('');
                    setPage(1);
                    setTimeout(() => fetchQuestions(1), 0);
                  }}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-red-500 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Results summary */}
        {hasSearched && (
          <div className="text-sm text-gray-500 mb-3 px-1">
            {total > 0
              ? `Showing ${startItem.toLocaleString()}-${endItem.toLocaleString()} of ${total.toLocaleString()} questions`
              : 'No questions found'}
          </div>
        )}

        {/* Questions table */}
        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading questions...</p>
          </div>
        ) : questions.length > 0 ? (
          <div className="bg-white rounded-xl overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Question</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">Category</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">Sub-Cat</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-14">Diff</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-14">Year</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-14">Ans</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {questions.map((q, idx) => (
                    <tr key={q.id || idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-400">{startItem + idx}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        <span title={q.question}>{truncate(q.question)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={capitalize(q.category)}>
                        {capitalize(q.category) || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={capitalize(q.sub_category)}>
                        {q.sub_category ? truncate(capitalize(q.sub_category), 20) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{q.difficulty || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{q.year || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center font-medium">
                        {INDEX_TO_LETTER[q.correct_answer] || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openEdit(q)}
                            className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                            title="Edit question"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(q)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete question"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : hasSearched ? (
          <div className="bg-white rounded-xl p-12 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
            <FileText size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No questions found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : null}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              style={{ boxShadow: 'var(--shadow-card)' }}
            >
              <ChevronLeft size={16} />
              Prev
            </button>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Page</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={page}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (val >= 1 && val <= totalPages) handlePageChange(val);
                }}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="flex items-center gap-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              style={{ boxShadow: 'var(--shadow-card)' }}
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ── Edit Modal ─────────────────────────────────────── */}
      {editQuestion && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 px-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mb-10">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Edit3 size={20} className="text-cyan-600" />
                  Edit Question
                </h2>
                <p className="text-xs text-gray-400 mt-1 font-mono">ID: {editQuestion.id}</p>
              </div>
              <button onClick={() => setEditQuestion(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Question text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                <textarea
                  value={editForm.question}
                  onChange={(e) => handleEditChange('question', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                />
              </div>

              {/* Options */}
              {['A', 'B', 'C', 'D', 'E'].map((letter, i) => (
                <div key={letter}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Option {letter}
                    {letter === 'E' && <span className="text-gray-400 ml-1">(optional)</span>}
                  </label>
                  <input
                    type="text"
                    value={editForm[`option${i}`]}
                    onChange={(e) => handleEditChange(`option${i}`, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                  />
                </div>
              ))}

              {/* Correct answer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer</label>
                <select
                  value={editForm.correctAnswer}
                  onChange={(e) => handleEditChange('correctAnswer', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                >
                  {['A', 'B', 'C', 'D', 'E'].map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>

              {/* Category & Subcategory */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={editForm.category}
                    onChange={(e) => handleEditChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category</label>
                  <input
                    type="text"
                    value={editForm.sub_category}
                    onChange={(e) => handleEditChange('sub_category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                  />
                </div>
              </div>

              {/* Year & Difficulty */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={editForm.year}
                    onChange={(e) => handleEditChange('year', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                  >
                    <option value="">Not set</option>
                    {YEARS.map((y) => (
                      <option key={y} value={y}>Year {y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={editForm.difficulty}
                    onChange={(e) => handleEditChange('difficulty', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                  >
                    {DIFFICULTIES.map((d) => (
                      <option key={d} value={d}>Level {d}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Explanation</label>
                <textarea
                  value={editForm.explanation}
                  onChange={(e) => handleEditChange('explanation', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-sm"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setEditQuestion(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Delete Question</h3>
            </div>

            <p className="text-gray-600 mb-2">
              Are you sure you want to delete this question? This cannot be undone.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-red-800 font-mono">
                "{truncate(deleteTarget.question, 100)}"
              </p>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminQuestionsPage;
